// src/components/preview/PracticeMicPanel.tsx
import React, { useEffect, useRef, useState } from "react";
import { Mic, Square, Info } from "lucide-react";
import { localMatch } from "../../lib/run-matcher";

/**
 * Preview-only microphone practice.
 * - Uses Web Speech API locally (no audio wiring, no server).
 * - On each FINAL result, runs the same matcher logic locally.
 * - Never dispatches Live events; never surfaces cues.
 */

type PracticeTop = {
  boardId: string;
  boardTitle: string;
  triggerText: string;
  stage: "strict" | "substring" | "tail" | "anchor" | "fuzzy" | "semantic";
  latencyMs: number;
};

export default function PracticeMicPanel({
  onMatch,
}: {
  onMatch: (r: { rawText: string; top?: PracticeTop }) => void;
}) {
  const [supported, setSupported] = useState<boolean>(false);
  const [listening, setListening] = useState<boolean>(false);
  const [transcript, setTranscript] = useState<string>("");

  const recRef = useRef<any>(null);
  const partialRef = useRef<string>("");

  useEffect(() => {
    const w: any = window as any;
    const SR = w.SpeechRecognition || w.webkitSpeechRecognition;
    setSupported(!!SR);
  }, []);

  // Start/Stop handlers
  const start = () => {
    if (!supported || listening) return;
    const w: any = window as any;
    const SR = w.SpeechRecognition || w.webkitSpeechRecognition;
    if (!SR) return;

    const rec = new SR();
    rec.lang = "en-US";
    rec.interimResults = true;
    rec.continuous = true;

    rec.onresult = async (ev: any) => {
      let finalText = "";
      let interim = "";
      for (let i = ev.resultIndex; i < ev.results.length; i++) {
        const res = ev.results[i];
        const txt = res[0]?.transcript || "";
        if (res.isFinal) finalText += txt + " ";
        else interim += txt + " ";
      }
      if (interim) {
        partialRef.current = interim.trim();
        setTranscript((prev) => (finalText ? (finalText + interim).trim() : (prev.split("\n")[0] || "") + "\n" + interim.trim()));
      }
      if (finalText) {
        const utter = finalText.trim();
        setTranscript((prev) => (prev ? (utter + "\n") : utter + "\n"));
        // Run local matcher once per final
        const t0 = performance.now();
        const top = await localMatch(utter);
        const t1 = performance.now();
        onMatch({
          rawText: utter,
          top: top
            ? {
                boardId: top.id,
                boardTitle: top.title,
                triggerText: top.trigger,
                stage: top.stage,
                latencyMs: Math.max(0, Math.round(t1 - t0)),
              }
            : undefined,
        });
      }
    };

    rec.onerror = () => {
      // graceful stop
      try { rec.stop(); } catch {}
      setListening(false);
    };
    rec.onend = () => {
      setListening(false);
    };

    try {
      rec.start();
      recRef.current = rec;
      setListening(true);
    } catch {
      setListening(false);
    }
  };

  const stop = () => {
    if (!listening) return;
    try { recRef.current?.stop?.(); } catch {}
    setListening(false);
  };

  return (
    <div className="vstack-sm">
      {!supported && (
        <div className="hint compact" role="note" aria-live="polite">
          <Info size={16} className="hint-icon" aria-hidden />
          <div className="hint-content">
            Microphone practice needs a compatible browser. You can still use the typed test above.
          </div>
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
        <button
          className="button button-secondary"
          onClick={listening ? stop : start}
          aria-pressed={listening}
          title={listening ? "Stop Practice Mic" : "Start Practice Mic"}
        >
          {listening ? <Square size={16} style={{ marginRight: 8 }} /> : <Mic size={16} style={{ marginRight: 8 }} />}
          {listening ? "Stop" : "Start"} Practice Mic
        </button>

        <span
          className={`practice-pill ${listening ? "listening" : ""}`}
          aria-live="polite"
          role="status"
          title="Mic here never triggers cues in Live"
        >
          Preview-only • {listening ? "Listening…" : "Idle"}
        </span>
      </div>

      <div className="card transcript-card" style={{ minHeight: 64 }}>
        <div className="muted small" style={{ marginBottom: 4 }}>Transcript (local)</div>
        <div style={{ whiteSpace: "pre-wrap" }}>{transcript || <span className="placeholder-text">Speak a phrase like, “Can you walk me through your background?”</span>}</div>
      </div>
    </div>
  );
}
